import { EmptyElementOptions, IElement, IPage } from '../types'

import { flattenDeep } from 'lodash'
import { ELEMENTS_LOOKUP } from '../const'

export const floatingElementsSet = new Set([
  ELEMENTS_LOOKUP.INITIALS_FIELD,
  ELEMENTS_LOOKUP.SIGNATURE,
  ELEMENTS_LOOKUP.DATE_FIELD,
  ELEMENTS_LOOKUP.CHECKBOX,
  ELEMENTS_LOOKUP.TEXT_FIELD,
  ELEMENTS_LOOKUP.SAVE_CARD_DETAILS,
])

export const blockElementsSet = new Set([
  ELEMENTS_LOOKUP.TEXT,
  ELEMENTS_LOOKUP.IMAGE,
  ELEMENTS_LOOKUP.VIDEO,
  ELEMENTS_LOOKUP.TABLE,
  ELEMENTS_LOOKUP.PRODUCT_LIST,
])

/**
 * Returns a flat array of all children elements across all pages
 * From each page it considers elements present in
 * 1. page.children
 * 2. page.component.options.headers.children
 * 3. page.component.options.footers.children
 * @param pages
 * @returns
 */
export const flatChildrens = (pages: IPage[]): IElement[] => {
  const extractChildren = (element: IElement): IElement[] => {
    if (element.type === ELEMENTS_LOOKUP.ROW) {
      return flattenDeep(element.children?.map(column => column.children) || [])
    }
    return element.children || []
  }

  const extractSectionChildren = (
    section: IElement<EmptyElementOptions> | undefined
  ): IElement[] => {
    if (!section) return []
    // Include the section itself
    const result = [section]
    // Include its direct children
    if (section.children) {
      result.push(...section.children)
    }
    // Include nested children
    if (section.children) {
      result.push(
        ...flattenDeep(section.children.map(child => extractChildren(child)))
      )
    }
    return result
  }

  return flattenDeep(
    pages.flatMap(page => [
      // Include page's direct children
      ...(page.children?.flatMap(element => [
        element,
        ...extractChildren(element),
      ]) || []),
      // Include header section and its children
      ...extractSectionChildren(page.component.options.headers),
      // Include footer section and its children
      ...extractSectionChildren(page.component.options.footers),
    ])
  )
}

/**
 * Returns all block elements in the document (excluding nested block elements) which are one of
 * TEXT, IMAGE, VIDEO, TABLE, PRODUCT_LIST
 * @param pages
 * @returns
 */
export const flatBlockChildrens = (pages: IPage[]) => {
  return pages.reduce((acc, page) => {
    page.children?.forEach(element => {
      if (element.type === ELEMENTS_LOOKUP.ROW) {
        element.children?.forEach(column => {
          column.children?.forEach(child => {
            if (blockElementsSet.has(child.type)) {
              acc.push(child)
            }
            if (child?.children && child?.children?.length > 0) {
              child.children.forEach(grandChild => {
                if (blockElementsSet.has(grandChild.type)) {
                  acc.push(grandChild)
                }
              })
            }
          })
        })
      } else {
        if (blockElementsSet.has(element.type)) {
          acc.push(element)
        }
        if (element?.children && element?.children?.length > 0) {
          element.children.forEach(child => {
            if (blockElementsSet.has(child.type)) {
              acc.push(child)
            }
          })
        }
      }
    })
    return acc
  }, [] as IElement[])
}
